import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FetcherWithComponents, useFetcher } from "react-router-dom";

import { RouteErrors } from "routes/data-routes/types";
import { useAuthenticationVersion } from "providers/AuthenticationProvider";
import { useFetcherDataCache } from "providers/FetcherDataCacheProvider";


export type FetcherReloadFunc = (clearValueWhileReloading?: boolean) => void;

function useFetcherDataValue<T>({ fetcherKey, initialValue }: { fetcherKey: string | undefined, initialValue: T }) {
  const fetcherDataCacheContext = useFetcherDataCache();
  const useCache = useMemo(() => !!fetcherDataCacheContext && !!fetcherKey, [ fetcherDataCacheContext, fetcherKey ]);
  const [ value, setValue ] = useState<T | undefined>(useCache ? (initialValue ?? fetcherDataCacheContext?.getFetcherData(fetcherKey!)) : initialValue);

  if (useCache) {
    return [ value, (newValue: (T | undefined) | ((oldValue: T | undefined) => T | undefined)) => {
      const valueToStore = newValue instanceof Function ? newValue(value) : newValue;
      fetcherDataCacheContext?.cacheFetcherData(fetcherKey!, valueToStore);
      setValue(valueToStore);
    } ] as const;
  } else {
    return [ value, setValue ] as const;
  }
}

export function useFetcherData<VT, DT = VT>(
  {
    fetcherKey,
    fetcherHref,
    performInitialFetch = true,
    reloadOnFocus = false,
    clearBeforeFocusReload = false,
    reloadOnAuthenticationChange = true,
    cache = true,
  }:
  {
    fetcherKey?: string,
    fetcherHref: string,
    performInitialFetch?: boolean,
    reloadOnFocus?: boolean,
    clearBeforeFocusReload?: boolean,
    reloadOnAuthenticationChange?: boolean,
    /**
     * If true and fetcherKey is also provided, data will be stored in FetcherDataCacheProvider's cache, so
     * this hook will be able to return stale data after processing action errors, even if navigation has
     * happened and the component tree got re-rendered.
     */
    cache?: boolean,
  }
): {
  value: VT | undefined,
  error: RouteErrors.ActionError | undefined,
  reload: FetcherReloadFunc,
  fetcher: FetcherWithComponents<DT | RouteErrors.RouterActionError>
} {
  const authenticationVersion = useAuthenticationVersion();
  // in case we have to reload data after re-authentication, use a new fetcher key
  const versionedFetcherKey = useMemo(
    () => (fetcherKey && reloadOnAuthenticationChange) ? (fetcherKey + "-v" + authenticationVersion.version) : fetcherKey,
    [ authenticationVersion, fetcherKey, reloadOnAuthenticationChange ]
  );

  const fetcher = useFetcher<DT | RouteErrors.RouterActionError>({ key: versionedFetcherKey });
  const fetcherError = useMemo(() => (fetcher.data as RouteErrors.RouterActionError)?.error, [ fetcher.data ]);
  const fetcherData = useMemo(() => fetcherError ? undefined : fetcher.data as DT, [ fetcher.data, fetcherError ]);

  // use the versioned fetcher key for caching. <FetcherDataCacheProvider> will automatically clear itself after
  // authentication version changes, but we can't guarantee it's always used wrapped inside an <AuthenticationProvider>
  // and if not, then the values won't be cleared automatically.
  const [ value, setValue ] = useFetcherDataValue<VT | undefined>({
    fetcherKey: cache ? versionedFetcherKey : undefined,
    initialValue: fetcherData as unknown as VT
  });

  // initial load
  useEffect(() => {
    if (performInitialFetch && (fetcher.state === "idle") && !fetcherData && !fetcherError) {
      fetcher.load(fetcherHref);
    }
  }, [ fetcher, fetcherData, fetcherError, fetcherHref, performInitialFetch ]);

  const reload = useCallback((clearValueWhileReloading: boolean = true) => {
    if (clearValueWhileReloading) {
      setValue(undefined);
    }
    fetcher.load(fetcherHref);
    // depends on load, not fetcher: fetcher changes on every load, and a changing reload re-triggers effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ fetcher.load, fetcherHref ]);

  useEffect(() => {
    // keep stale data if error received
    if (!fetcherError) {
      setValue(fetcherData as unknown as VT);
    }
  }, [ fetcherData, fetcherError, setValue ]);

  const focusListenersAdded = useRef<boolean>(false);
  // focus handling
  useEffect(() => {
    if (!reloadOnFocus || focusListenersAdded.current) {
      // feature disabled or data is read from a data route and not from a fetcher
      return;
    }

    function onFocusGained() {
      reload(clearBeforeFocusReload);
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        reload(clearBeforeFocusReload);
      }
    }

    window.addEventListener("focus", onFocusGained);
    document.addEventListener("visibilitychange", onVisibilityChange);
    focusListenersAdded.current = true;

    return () => {
      window.removeEventListener("focus", onFocusGained);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      focusListenersAdded.current = false;
    }
  }, [ reload, reloadOnFocus, clearBeforeFocusReload ]);


  return {
    value,
    error: fetcherError,
    reload: reload,
    fetcher: fetcher
  };
}
